// Triple-redundancy refresh token storage for iOS PWA resilience.
// iOS WebKit purges localStorage, httpOnly cookies, and IndexedDB on
// slightly different schedules under Intelligent Tracking Prevention,
// so writing the refresh token to all three increases the chance at
// least one survives a long PWA idle.
//
// - Cookie: written server-side (httpOnly, Secure, max-age ~10y). JS can't read it.
// - localStorage: synchronous, easy to read, first to be purged by ITP.
// - IndexedDB: survives longer than localStorage in some iOS scenarios.

const DB_NAME = "expenser-auth";
const STORE = "kv";
const REFRESH_KEY = "refresh_token";

function _open() {
  return new Promise((resolve, reject) => {
    try {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    } catch (e) {
      reject(e);
    }
  });
}

async function _put(key, value) {
  try {
    const db = await _open();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put(value, key);
      tx.oncomplete = () => { db.close(); resolve(true); };
      tx.onerror = () => { db.close(); reject(tx.error); };
    });
  } catch { return false; }
}

async function _get(key) {
  try {
    const db = await _open();
    return await new Promise((resolve) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).get(key);
      req.onsuccess = () => { db.close(); resolve(req.result || ""); };
      req.onerror = () => { db.close(); resolve(""); };
    });
  } catch { return ""; }
}

async function _del(key) {
  try {
    const db = await _open();
    return await new Promise((resolve) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).delete(key);
      tx.oncomplete = () => { db.close(); resolve(true); };
      tx.onerror = () => { db.close(); resolve(false); };
    });
  } catch { return false; }
}

// Public API — writes to BOTH localStorage and IndexedDB.
export function saveRefreshToken(value) {
  try { if (value) localStorage.setItem(REFRESH_KEY, value); } catch { /* quota / private mode */ }
  // Fire and forget — IndexedDB write is async and we don't want to block.
  _put(REFRESH_KEY, value || "");
}

// Reads localStorage first (sync), falls back to IndexedDB (async) if missing.
// Always rehydrates localStorage from IndexedDB when we recover from there.
export async function loadRefreshToken() {
  let ls = "";
  try { ls = localStorage.getItem(REFRESH_KEY) || ""; } catch { /* ignore */ }
  if (ls) return ls;
  const idb = await _get(REFRESH_KEY);
  if (idb) {
    try { localStorage.setItem(REFRESH_KEY, idb); } catch { /* ignore */ }
  }
  return idb;
}

export function clearRefreshToken() {
  try { localStorage.removeItem(REFRESH_KEY); } catch { /* ignore */ }
  _del(REFRESH_KEY);
}
